import type {
  DayRecord,
  FocusSession,
  Habit,
  HabitCompletion,
  LocalDateKey,
  TaskOccurrence,
  TaskTemplate,
  XpEvent
} from '@/types';
import type { DisciplineDatabase } from '@/db/database';
import { calculateDayProgress } from './tasks';
import { calculateStreak } from './streaks';
import { compareDateKeys, enumerateDateKeys } from './dates';
import { createId } from './ids';
import { occursOnDate, recurrenceDatesBetween } from './recurrence';

export interface OccurrenceGenerationResult {
  occurrences: TaskOccurrence[];
  occurrenceKeys: Set<string>;
}

export interface RolloverOptions {
  lastOpenedDate?: LocalDateKey;
  today: LocalDateKey;
  now?: Date;
}

export interface RolloverResult {
  processedDates: LocalDateKey[];
  generatedOccurrenceIds: string[];
  missedOccurrenceIds: string[];
  archivedDates: LocalDateKey[];
}

export function taskOccurrenceKey(templateId: string, date: LocalDateKey): string {
  return `${templateId}:${date}`;
}

export function isTemplateDueOnDate(template: TaskTemplate, date: LocalDateKey): boolean {
  return (
    template.active &&
    compareDateKeys(date, template.effectiveFrom) >= 0 &&
    (!template.effectiveUntil || compareDateKeys(date, template.effectiveUntil) <= 0) &&
    occursOnDate(template.recurrence, date)
  );
}

export function occurrenceFromTemplate(
  template: TaskTemplate,
  date: LocalDateKey,
  createdAt = new Date().toISOString()
): TaskOccurrence {
  return {
    id: createId(),
    occurrenceKey: taskOccurrenceKey(template.id, date),
    templateId: template.id,
    templateRevision: template.revision,
    title: template.title,
    description: template.description,
    taskType: template.taskType,
    category: template.category,
    priority: template.priority,
    status: 'planned',
    date,
    ...(template.defaultStartTime ? { startTime: template.defaultStartTime } : {}),
    ...(template.defaultEndTime ? { endTime: template.defaultEndTime } : {}),
    ...(template.estimatedMinutes !== undefined
      ? { estimatedMinutes: template.estimatedMinutes }
      : {}),
    ...(template.defaultTargetValue !== undefined
      ? { targetValue: template.defaultTargetValue }
      : {}),
    ...(template.unit ? { unit: template.unit } : {}),
    currentValue: 0,
    strictMode: template.strictMode,
    ...(template.projectId ? { projectId: template.projectId } : {}),
    notes: template.notes,
    sortOrder: template.sortOrder,
    createdAt,
    updatedAt: createdAt
  };
}

export function generateOccurrencesForDate(
  templates: readonly TaskTemplate[],
  date: LocalDateKey,
  existingOccurrenceKeys: Iterable<string> = [],
  createdAt = new Date().toISOString()
): OccurrenceGenerationResult {
  const keys = new Set(existingOccurrenceKeys);
  const occurrences: TaskOccurrence[] = [];
  for (const template of templates) {
    const key = taskOccurrenceKey(template.id, date);
    if (!keys.has(key) && isTemplateDueOnDate(template, date)) {
      occurrences.push(occurrenceFromTemplate(template, date, createdAt));
      keys.add(key);
    }
  }
  return { occurrences, occurrenceKeys: keys };
}

export function deferredOccurrenceForDate(
  source: TaskOccurrence,
  date: LocalDateKey,
  createdAt = new Date().toISOString()
): TaskOccurrence {
  return {
    ...source,
    id: createId(),
    occurrenceKey: `deferred:${source.id}:${date}`,
    date,
    status: 'planned',
    currentValue: source.currentValue,
    deferredFromDate: source.date,
    sourceOccurrenceId: source.id,
    createdAt,
    updatedAt: createdAt,
    startedAt: undefined,
    completedAt: undefined,
    skippedAt: undefined,
    skipReason: undefined,
    deferredToDate: undefined
  };
}

function isMinutesUnit(unit: string | undefined): boolean {
  const normalized = unit?.trim().toLowerCase();
  return (
    normalized === 'minute' ||
    normalized === 'minutes' ||
    normalized === 'minuten' ||
    normalized === 'min'
  );
}

export function buildDayRecord(
  date: LocalDateKey,
  occurrences: readonly TaskOccurrence[],
  focusSessions: readonly FocusSession[],
  xpEvents: readonly XpEvent[],
  closed: boolean,
  nowIso: string,
  existing?: DayRecord
): DayRecord {
  const progress = calculateDayProgress(occurrences);
  const minutesFor = (category: TaskOccurrence['category']): number =>
    occurrences
      .filter((item) => item.category === category && isMinutesUnit(item.unit))
      .reduce((sum, item) => sum + item.currentValue, 0);
  const valueFor = (unit: string): number =>
    occurrences
      .filter((item) => item.unit === unit)
      .reduce((sum, item) => sum + item.currentValue, 0);
  const focusMinutes = focusSessions.reduce((total, session) => total + session.creditedMinutes, 0);

  return {
    id: existing?.id ?? createId(),
    date,
    isClosed: closed,
    ...(closed ? { closedAt: existing?.closedAt ?? nowIso } : {}),
    plannedTasks: progress.plannedCount,
    completedTasks: progress.completedCount,
    missedTasks: progress.missedCount,
    mandatoryTasks: progress.mandatoryCount,
    completedMandatoryTasks: progress.completedMandatoryCount,
    focusMinutes,
    schoolMinutes: minutesFor('school'),
    frenchMinutes: minutesFor('french'),
    youtubeMinutes: minutesFor('youtube'),
    novelChapters: valueFor('chapters'),
    pushUps: valueFor('push-ups'),
    squats: valueFor('squats'),
    earnedXp: xpEvents.reduce((sum, event) => sum + event.amount, 0),
    completionRate: progress.completionPercent,
    createdAt: existing?.createdAt ?? nowIso,
    updatedAt: nowIso
  };
}

function habitStatusForOccurrence(occurrence: TaskOccurrence): HabitCompletion['status'] {
  return occurrence.status === 'completed' ? 'completed' : 'missed';
}

function shouldMarkHabitMissed(occurrence: TaskOccurrence): boolean {
  return ['planned', 'active', 'paused', 'skipped'].includes(occurrence.status);
}

async function syncHabitCompletion(
  database: DisciplineDatabase,
  habit: Habit,
  occurrence: TaskOccurrence,
  nowIso: string
): Promise<void> {
  const completionKey = `${habit.id}:${occurrence.date}`;
  if (await database.habitCompletions.where('completionKey').equals(completionKey).first()) return;
  const status = habitStatusForOccurrence(occurrence);
  await database.habitCompletions.add({
    id: createId(),
    completionKey,
    habitId: habit.id,
    occurrenceId: occurrence.id,
    date: occurrence.date,
    status,
    value: occurrence.currentValue,
    ...(status === 'completed' ? { completedAt: occurrence.completedAt ?? nowIso } : {}),
    createdAt: nowIso,
    updatedAt: nowIso
  });
}

export async function runDayRollover(
  database: DisciplineDatabase,
  options: RolloverOptions
): Promise<RolloverResult> {
  const now = options.now ?? new Date();
  const nowIso = now.toISOString();
  const start =
    options.lastOpenedDate && compareDateKeys(options.lastOpenedDate, options.today) <= 0
      ? options.lastOpenedDate
      : options.today;
  const processedDates = enumerateDateKeys(start, options.today);
  const generatedOccurrenceIds: string[] = [];
  const missedOccurrenceIds: string[] = [];
  const archivedDates: LocalDateKey[] = [];

  await database.transaction(
    'rw',
    [
      database.taskTemplates,
      database.taskOccurrences,
      database.focusSessions,
      database.dayRecords,
      database.habits,
      database.habitCompletions,
      database.streakRecords,
      database.xpEvents
    ],
    async () => {
      const templates = await database.taskTemplates.toArray();
      const habits = await database.habits.filter((habit) => habit.active).toArray();
      const habitByTemplateId = new Map(habits.map((habit) => [habit.taskTemplateId, habit]));

      for (const date of processedDates) {
        let occurrences = await database.taskOccurrences.where('date').equals(date).toArray();
        const generated = generateOccurrencesForDate(
          templates,
          date,
          occurrences.map((occurrence) => occurrence.occurrenceKey),
          nowIso
        ).occurrences;
        if (generated.length > 0) {
          await database.taskOccurrences.bulkAdd(generated);
          generatedOccurrenceIds.push(...generated.map((occurrence) => occurrence.id));
          occurrences = [...occurrences, ...generated];
        }

        if (date === options.today) {
          const deferredSources = await database.taskOccurrences
            .filter(
              (occurrence) =>
                occurrence.status === 'deferred' && occurrence.deferredToDate === options.today
            )
            .toArray();
          const existingKeys = new Set(occurrences.map((occurrence) => occurrence.occurrenceKey));
          const deferred = deferredSources
            .map((source) => deferredOccurrenceForDate(source, options.today, nowIso))
            .filter((occurrence) => !existingKeys.has(occurrence.occurrenceKey));
          if (deferred.length > 0) {
            await database.taskOccurrences.bulkAdd(deferred);
            generatedOccurrenceIds.push(...deferred.map((occurrence) => occurrence.id));
            occurrences = [...occurrences, ...deferred];
          }
        }

        const isPast = compareDateKeys(date, options.today) < 0;
        if (isPast) {
          for (const occurrence of occurrences) {
            const habit = occurrence.templateId
              ? habitByTemplateId.get(occurrence.templateId)
              : undefined;
            const shouldMarkMissed = habit
              ? shouldMarkHabitMissed(occurrence)
              : ['planned', 'active', 'paused'].includes(occurrence.status);
            if (shouldMarkMissed) {
              occurrence.status = 'missed';
              occurrence.updatedAt = nowIso;
              await database.taskOccurrences.put(occurrence);
              missedOccurrenceIds.push(occurrence.id);
            }
            if (habit) await syncHabitCompletion(database, habit, occurrence, nowIso);
          }
        }

        const existingDay = await database.dayRecords.where('date').equals(date).first();
        if (!existingDay?.isClosed || !isPast) {
          const focusSessions = await database.focusSessions.where('date').equals(date).toArray();
          const xpEvents = await database.xpEvents.where('date').equals(date).toArray();
          const dayRecord = buildDayRecord(
            date,
            occurrences,
            focusSessions,
            xpEvents,
            isPast || existingDay?.isClosed === true,
            nowIso,
            existingDay
          );
          await database.dayRecords.put(dayRecord);
          if (isPast) archivedDates.push(date);
        }
      }

      for (const habit of habits) {
        const completions = await database.habitCompletions
          .where('habitId')
          .equals(habit.id)
          .toArray();
        const scheduledDates = recurrenceDatesBetween(
          habit.recurrence,
          habit.startedOn,
          options.today
        );
        const streak = calculateStreak(completions, options.today, scheduledDates);
        const existing = await database.streakRecords.where('habitId').equals(habit.id).first();
        await database.streakRecords.put({
          id: existing?.id ?? createId(),
          habitId: habit.id,
          calculatedThrough: options.today,
          ...streak,
          createdAt: existing?.createdAt ?? nowIso,
          updatedAt: nowIso
        });
      }
    }
  );

  return {
    processedDates,
    generatedOccurrenceIds,
    missedOccurrenceIds,
    archivedDates
  };
}
